import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {
  FormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PaymentProfileStatus } from '../../../models/_enum-collections';
import { CommonInputModel } from '../../../models/common-input.model';
import {
  PartnerPaymentDetails,
  FiscalYear
} from '../../../models/partner-payment-details.model';
import { PaymentProfileProgramType } from '../../../models/payment-profile-program-type.model';
import { UpdatePayee } from '../../../models/update-payee.model';
import { AppConstants } from '../../common/app-constants';
import { CommonSharedModule } from '../../common/shared.module';
import { DashboardService } from '../../services/dashboard.service';
import { OsmosisEditService } from '../../services/osmosis-edit.service';
import { ValidationService } from '../../services/validation.service';
import { VendorService } from '../../services/vendor.service';
import { AddVendorComponent } from '../add-vendor/add-vendor.component';

@Component({
  selector: 'app-payment-information',
  templateUrl: './payment-information.component.html',
  styleUrls: ['./payment-information.component.scss'],
  standalone: true,
  imports: [
    AddVendorComponent,
    CommonModule,
    RouterModule,
    FormsModule,
    CommonSharedModule
  ]
})
export class PaymentInformationComponent implements OnInit {
  @Input() partnerPaymentDetails!: PartnerPaymentDetails;
  @Input() searchedMpnId!: number;
  @Input() fiscalYear!: FiscalYear[];

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onPayeeInfoUpdate = new EventEmitter<any>();
  @ViewChild('addVendorComponent', { static: false })
  addVendorComponent!: AddVendorComponent;
  currentFiscalyear = this.getCurrentFiscalYear();
  selectedFiscalYear: FiscalYear = {
    fiscalYear: this.currentFiscalyear,
    fiscalYearName: 'FY' + (this.currentFiscalyear % 100)
  };
  isEditUpdatePayeeInfo = false;
  vendorId!: string;
  supplierId!: string;
  payeeUpdateForm!: FormGroup;
  msgsUsageDetails!: any[];
  showResponse!: boolean;
  isMCAPAgreed = true;
  isEventAgreed = true;
  isMSAIPAgreed = true;
  isMSSPAgreed = true;
  isOperatorAgreed = true;
  mcapFiscalYear = false;

  programTypes!: PaymentProfileProgramType[];
  selectedProgramType!: PaymentProfileProgramType;
  defaultProgramType!: PaymentProfileProgramType;

  constructor(
    private formBuilder: FormBuilder,
    private osmEdit: OsmosisEditService,
    private ds: DashboardService,
    private validationService: ValidationService,
    private paymentProfileService: VendorService
  ) {}

  OnFormGroup() {
    this.payeeUpdateForm = this.formBuilder.group({
      supplierId: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern('^[0-9]*$'),
          this.validationService.isInteger32
        ]
      ],
      vendorId: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          Validators.pattern('^[0-9]*$'),
          this.validationService.isInteger32
        ]
      ]
    });
    this.payeeUpdateForm.disable();
  }

  ngOnInit() {
    this.GetProgramAgreement(
      this.searchedMpnId,
      this.selectedFiscalYear.fiscalYear
    );
    this.getPaymentProfileProgramType();
    this.OnFormGroup();
    this.payeeUpdateForm.controls['supplierId'].setValue(
      this.partnerPaymentDetails.supplierId
    );
    this.payeeUpdateForm.controls['vendorId'].setValue(
      this.getVendorIdText(this.partnerPaymentDetails.vendorId)
    );
    this.partnerPaymentDetails.paymentProfileStatusDescription =
      this.partnerPaymentDetails.paymentProfileStatusDescription != null
        ? this.partnerPaymentDetails.paymentProfileStatusDescription
        : PaymentProfileStatus.Missing;

    this.defaultProgramType = {
      programTypeId: 0,
      programName: 'MCAP (Current Shared Profile)'
    };
    this.selectedProgramType = this.defaultProgramType;
  }

  get PaymentProfileStatus(): typeof PaymentProfileStatus {
    return PaymentProfileStatus;
  }

  getVendorIdText(vendorId): string {
    return vendorId ? vendorId : 'N/A';
  }

  editUpdatePayeeInfo() {
    this.msgsUsageDetails = [];

    this.isEditUpdatePayeeInfo = true;
    this.payeeUpdateForm.enable();

    this.payeeUpdateForm.controls['supplierId'].setValue(
      this.partnerPaymentDetails.supplierId
    );
    this.payeeUpdateForm.controls['vendorId'].setValue(
      this.partnerPaymentDetails.vendorId
    );
  }

  cancelEditUpdatePayeeInfo() {
    this.msgsUsageDetails = [];
    this.isEditUpdatePayeeInfo = false;
    this.payeeUpdateForm.disable();
  }

  UpdatePayeeInfoDetails() {
    if (this.isEditUpdatePayeeInfo) {
      const payeeInfo: UpdatePayee = {
        supplierId: this.payeeUpdateForm.controls['supplierId'].value,
        vendorId: this.payeeUpdateForm.controls['vendorId'].value,
        partnerId: this.searchedMpnId,
        programTypeId: this.selectedProgramType.programTypeId,
        isSharedProfile: this.selectedProgramType.programTypeId == 0
      };
      this.osmEdit.updatePayeeInformation(payeeInfo).subscribe(
        () => {
          this.partnerPaymentDetails.supplierId =
            this.payeeUpdateForm.controls['supplierId'].value;
          this.partnerPaymentDetails.vendorId =
            this.payeeUpdateForm.controls['vendorId'].value;
          this.showResponse = true;
          this.onPayeeInfoUpdate.emit({
            programType: this.selectedProgramType.programTypeId,
            finYear: this.currentFiscalyear.toString()
          });
          this.showMessage('Updated Successfully', 'success');
          this.isEditUpdatePayeeInfo = !this.isEditUpdatePayeeInfo;
        },
        error => {
          this.showResponse = true;
          this.showMessage(error.error.errorInfo, 'error');
        }
      );

      this.payeeUpdateForm.disable();
    } else {
      this.resetPayeeEdit();
    }
  }

  resetPayeeEdit(): void {
    this.vendorId = this.getVendorIdText(this.partnerPaymentDetails.vendorId);
    this.supplierId =
      this.partnerPaymentDetails.supplierId?.toString() ?? 'N/A';
  }

  showMessage(message: string, response: string) {
    this.msgsUsageDetails = [];
    this.msgsUsageDetails.push({ severity: response, detail: message });
  }

  get updatePayeeDetails(): Record<string, AbstractControl> {
    return this.payeeUpdateForm.controls;
  }

  showAddVendorDialog() {
    this.addVendorComponent.showDialog();
  }

  filterTable(event) {
    this.GetProgramAgreement(this.searchedMpnId, event.value.fiscalYear);
  }

  GetProgramAgreement(mpnId: any, year: number) {
    this.ds.GetProgramAgreement(mpnId, year).subscribe(x => {
      this.isEventAgreed = x.isEventAgreed;
      this.isMCAPAgreed = x.isMCAPAgreed;
      this.isMSAIPAgreed = x.isMSAIPAgreed;
      this.isMSSPAgreed = x.isMSSPAgreed;
      this.isOperatorAgreed = x.isOperatorAgreed;
      this.mcapFiscalYear = year == AppConstants.mcapFiscalYear;
    });
  }

  getStyleForPaymentProfileStatus(status: string | undefined) {
    if (!status) {
      status = PaymentProfileStatus.Missing;
    }

    return this.paymentProfileService.getStyleForPaymentProfileStatus(status);
  }

  getPaymentProfileStatus(status: string | undefined) {
    if (!status) {
      status = PaymentProfileStatus.Missing;
    }

    return this.paymentProfileService.getPaymentProfileStatus(status);
  }

  getPaymentProfileProgramType() {
    this.paymentProfileService
      .getPaymentProfileProgramType()
      .subscribe(result => {
        this.programTypes = result;
        this.programTypes.push(this.defaultProgramType);
        this.programTypes.sort(
          (a, b) => (a.programTypeId ?? 0) - (b.programTypeId ?? 0)
        );
        this.selectedProgramType = this.defaultProgramType;
      });
  }

  getCurrentFiscalYear() {
    const currentDate = new Date();
    if (currentDate.getMonth() < 6) {
      return currentDate.getFullYear();
    }
    return currentDate.getFullYear() + 1;
  }

  onPaymentProfileProgramTypeChange(event) {
    this.selectedProgramType = event.value;

    const commmonInputModel: CommonInputModel = {
      mpnid: this.searchedMpnId.toString(),
      programTypeId: this.selectedProgramType.programTypeId,
      year: this.currentFiscalyear.toString()
    };

    this.getPaymentProfileProgramTypeDetails(commmonInputModel);
  }

  getPaymentProfileProgramTypeDetails(commonInputModel: CommonInputModel) {
    this.ds
      .getPartnerAndPaymentDetails(commonInputModel)
      .subscribe(partnerPaymentDetails => {
        this.partnerPaymentDetails = partnerPaymentDetails;

        this.payeeUpdateForm.controls['supplierId'].setValue(
          partnerPaymentDetails.supplierId
        );

        this.payeeUpdateForm.controls['vendorId'].setValue(
          this.getVendorIdText(partnerPaymentDetails.vendorId)
        );
      });
  }
}
